// MENU-MEASURE-1 — the decision, and the rule that prevents the next one.
//
// SETTINGS-SPLIT-1 measured twenty four settings children and a third of that
// list came off. The other sixty two menu nodes had never been measured at all,
// which is the ceiling this wave raises: an item shows because nothing asked
// whether it should, and "nobody asked" is not the same as "everybody needs it".
//
// PURE AND UNMIRRORED, so mutation can run on it.

import { CATALOGUED_IDS, MENU_CATALOGUE, MENU_SECTIONS, type MenuItem } from './menuCatalogue'

/** What this account has done: table name to row count. Absent means zero. */
export type MenuActivity = Readonly<Record<string, number>>

/**
 * WHY AN ITEM IS ON SCREEN. Four answers, and every one of them is a reason a
 * person can read - which is the difference between a menu that was designed
 * and one that merely accumulated.
 */
export type ShownBecause =
  | { kind: 'day_one'; reason: string }
  | { kind: 'used'; table: string; rows: number }
  | { kind: 'unmeasurable'; reason: string }
  | { kind: 'section'; children: number }

export type HiddenBecause = { kind: 'never_used'; table: string }

export type MenuVerdict =
  | { shown: true; because: ShownBecause }
  | { shown: false; because: HiddenBecause }

/**
 * DOES THIS ITEM BELONG ON THIS ACCOUNT'S MENU?
 *
 * NEVER USED IS NOT NOT YET, and the whole of section 7 is this rule: an item
 * declared `dayOne` shows at zero, for its stated reason, because a brand new
 * account has used nothing and hiding what it is supposed to start with makes
 * an empty product emptier. Only an item with a MEASURE can be hidden, and only
 * when the measure genuinely read zero.
 *
 * An `unmeasurable` item always shows. That is the honest default: we did not
 * measure it, so we do not get to remove it.
 */
export function verdictFor(item: MenuItem, activity: MenuActivity): MenuVerdict {
  if (item.dayOne) return { shown: true, because: { kind: 'day_one', reason: item.dayOne } }
  if (item.unmeasurable) {
    return { shown: true, because: { kind: 'unmeasurable', reason: item.unmeasurable } }
  }
  if (item.measure) {
    const rows = activity[item.measure.table] ?? 0
    if (rows > 0) return { shown: true, because: { kind: 'used', table: item.measure.table, rows } }
    return { shown: false, because: { kind: 'never_used', table: item.measure.table } }
  }
  // Unreachable while the declaration guard passes: an item with none of the
  // three cannot be added. Shown rather than hidden, because an undeclared item
  // is our omission and a merchant should not pay for it.
  return { shown: true, because: { kind: 'unmeasurable', reason: 'No measure declared.' } }
}

/** The ids on screen for this account, children only. */
export function visibleMenuItems(activity: MenuActivity): MenuItem[] {
  return MENU_CATALOGUE.filter((item) => verdictFor(item, activity).shown)
}

/**
 * A SECTION SHOWS WHEN ONE OF ITS CHILDREN DOES. Derived, never declared, so a
 * section cannot disagree with the things inside it.
 */
export function visibleSections(activity: MenuActivity): string[] {
  const shown = new Set(visibleMenuItems(activity).map((i) => i.parent))
  // `home` and `settings` are not in the catalogue: home has no children and
  // settings is SETTINGS-SPLIT-1's, already measured by its own buckets. Both
  // stay, which is a statement rather than an oversight.
  return MENU_SECTIONS.filter((s) => s === 'home' || s === 'settings' || shown.has(s))
}

// ─── The rule that prevents the next one ────────────────────────────────

export interface UndeclaredItem {
  id: string
  why: string
}

/**
 * EVERY MENU ITEM DECLARES ITS MEASURE, OR WHY IT HAS NONE.
 *
 * Given the ids `navConfig` actually contains, this returns the ones the
 * catalogue does not answer for. A new menu item added without a declaration
 * appears here, and the guard fails - the same shape as a workflow trigger
 * having to declare why it is off.
 *
 * `exempt` carries the ids answered somewhere else, each with the reason it is
 * not this catalogue's business. It is a NAMED list, not a filter: a new id
 * cannot slip through by resembling one of them.
 */
export function undeclared(
  navIds: readonly string[],
  exempt: Readonly<Record<string, string>>,
): UndeclaredItem[] {
  const out: UndeclaredItem[] = []
  for (const id of navIds) {
    if (CATALOGUED_IDS.has(id)) continue
    if (id in exempt) continue
    out.push({
      id,
      why: 'no measure, no day-one reason, and no written reason it cannot be measured',
    })
  }
  return out
}

/**
 * Declared exactly once, or the two answers could disagree.
 *
 * TAKES THE CATALOGUE rather than reading the module's. A detector asserted
 * only against the real catalogue is asserted only to return nothing, and one
 * that always returned nothing would pass that for ever - which mutation caught
 * here, not review.
 */
export function duplicateDeclarations(catalogue: readonly MenuItem[] = MENU_CATALOGUE): string[] {
  const seen = new Map<string, number>()
  for (const item of catalogue) seen.set(item.id, (seen.get(item.id) ?? 0) + 1)
  return [...seen].filter(([, n]) => n > 1).map(([id]) => id)
}

/** An item declaring two answers at once, or none, is ambiguous about why it is
 *  on screen. Same reason for the parameter as above. */
export function overDeclared(catalogue: readonly MenuItem[] = MENU_CATALOGUE): string[] {
  return catalogue
    .filter((i) => [i.measure, i.dayOne, i.unmeasurable].filter(Boolean).length !== 1)
    .map((i) => i.id)
}
